import { FieldState } from './form-state';

export type Validator = (value: string) => ValidationResult;

export const validateFormField = (field: FieldState, value: string | undefined) => {
  field.setError(undefined);
  const isRequired = field.required || (field.requiredIf && field.requiredIf(value));
  const isEmpty = typeof value === 'string' ? value.trim().length === 0 : true;

  if (typeof value === 'undefined') {
    if (isRequired) {
      field.setError(`${field.visibleName} is required.`);
      return false;
    }
    return true;
  }

  if (field.type === 'text' && isRequired && isEmpty) {
    // If the field is required and is empty, this is an error.
    field.setError(`${field.visibleName} is required.`);
    return false;
  }
  
  if (field.type === 'boolean' && value != 'true' && isRequired) {
    field.setError(`${field.visibleName} must be checked.`);
    return false;
  }

  // Non-required fields don't need validation if they are empty.
  if (!field.validations || !isRequired && isEmpty) return true;

  for (const validator of field.validations) {
    const result = validator(value);
    if (typeof result === 'boolean' ? !result : !result.valid) {
      field.setError(typeof result === 'boolean' ? 'Invalid input' : result.reason || 'Invalid input');
      return false;
    }
  }
  return true;
};

export const validateForm = (formFields: Record<string, FieldState>) => {
  let formIsValid = true;
  for (const field in formFields) {
    formIsValid = validateFormField(formFields[field], formFields[field].value) && formIsValid;
  }
  return formIsValid;
};

export type ValidationResult = boolean | {
  valid: boolean,
  reason?: string
}

const matchRegex = (regex: RegExp, { explanation } : {explanation?: string } = {}) => {
  return (value: string) => {
    const result: ValidationResult = { valid: regex.test(value) };
    if (!result.valid && explanation) result.reason = explanation;
    return result;
  };
};

const hasLength = (
  length: number,
  { explanation, excludeWhitespace } : { explanation?: string, excludeWhitespace?: boolean } = {},
) => {
  return (value: string) => {
    const valueLength = excludeWhitespace
      ? value.replace(/\s/g, '').length
      : value.length;
    const result: ValidationResult = { valid: valueLength === length };
    if (!result.valid) {
      if (explanation) result.reason = explanation;
      else result.reason = `Length must be ${length}.`;
    }
    return result;
  };
};

const hasMinLength = (
  length: number,
  { explanation, excludeWhitespace } : { explanation?: string, excludeWhitespace?: boolean } = {},
) => {
  return (value: string) => {
    const valueLength = excludeWhitespace
      ? value.replace(/\s/g, '').length
      : value.length;
    const result: ValidationResult = { valid: valueLength >= length };
    if (!result.valid) {
      if (explanation) result.reason = explanation;
      else result.reason = `Length must be greater than or equal to ${length}.`;
    }
    return result;
  };
};

const isNumeric = ({ allowWhitespace }: { allowWhitespace?: boolean } = {}) => {
  return (value: string) => {
    const result: ValidationResult = allowWhitespace
      ? matchRegex(/^[0-9 ]*$/)(value)
      : matchRegex(/^[0-9]*$/)(value);
    if (!result.valid) result.reason = 'Must contain only numbers.';
    return result;
  };
};

const isNumber = () => {
  return (value: string) => {
    if (isNaN(Number.parseFloat(value))) {
      return {
        valid: false,
        reason: 'Must be a number.',
      };
    }
    return { valid: true };
  };
};

const isInteger = () => {
  return (value: string) => {
    if (!/^-?\d+$/.test(value)) {
      return {
        valid: false,
        reason: 'Must be an integer.',
      };
    }
    return { valid: true };
  };
};

const isEmail = () => {
  return (value: string) => {
    const result = matchRegex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)(value);
    if (!result.valid) result.reason = 'Must be a valid email.';
    return result;
  };
};

const minValue = (min: number) => {
  return (value: string) => {
    const valueIsNumeric = isNumber()(value);
    if (!valueIsNumeric.valid) return valueIsNumeric;
    const valid = Number.parseFloat(value) >= min;
    const reason = valid ? '' : `Must be greater than or equal to ${min}`;
    return { valid, reason };
  };
};

const maxValue = (max: number) => {
  return (value: string) => {
    const valueIsNumeric = isNumber()(value);
    if (!valueIsNumeric.valid) return valueIsNumeric;
    const valid = Number.parseFloat(value) <= max;
    const reason = valid ? '' : `Must be less than or equal to ${max}`;
    return { valid, reason };
  };
};

export const Validators = {
  matchRegex,
  hasLength,
  hasMinLength,
  isNumeric,
  isEmail,
  minValue,
  maxValue,
  isNumber,
  isInteger,
};
